// The theme tiles (2026-10-18; one model 2026-10-20, docs/plans/theme-layers.md): Horizon, the synced
// palettes (Midnight … Halloween … Paper, the Catppuccin presets, your own) and the person's saved
// themes are the same list on every layer (this device, me, this workspace). A tile is a theme; the
// brightness is picked apart from it. Pure, so it is tested.
import {
  CUSTOM_THEME,
  PRESETS,
  THEMES,
  THEME_DEFAULT_ACCENT,
  THEME_SURFACES,
  hueOfColour,
  presetFor,
  type Hue,
  type PalettePreset,
  type PaletteRecord,
  type Theme,
} from '@/lib/colour'
import type { Look, StudioLook } from '@/lib/look'
import type { ThemeRef } from '@/lib/themeLayers'
import type { ThemeProfileResource } from '@/kinds/themeprofile/apply'
import { mayEdit } from '@/lib/themes/profiles'

/** The tile key for the dice / a hand-made palette (the `custom` theme with no preset). */
export const OWN_TILE = 'random'

/** The tile key of Horizon, the default theme. */
export const HORIZON_TILE = 'horizon'

export type ThemeChoice =
  | { horizon: true }
  | { theme: Theme }
  | { preset: PalettePreset }
  /** a palette of one's own: the dice or the colour editor; `accent` is the hue it falls back to */
  | { own: PaletteRecord; accent: Hue }
  /** a saved theme: its ThemeProfile's id (the worn snapshot's, which may be gone) and the spec to wear */
  | { saved: { profile?: string; spec: unknown } }

/** What a tap writes into a layer: the theme and, for a palette, the accent that goes with it. */
export interface ThemePick {
  theme: ThemeRef
  /** a palette brings its accent; Horizon and a saved theme leave the accent to the layers */
  colour?: Hue
}

export const themePick = (choice: ThemeChoice): ThemePick => {
  if ('horizon' in choice) return { theme: { kind: 'horizon' } }
  if ('saved' in choice)
    return { theme: { kind: 'studio', profile: choice.saved.profile, spec: choice.saved.spec } }
  if ('theme' in choice)
    return {
      theme: { kind: 'palette', theme: choice.theme },
      colour: THEME_DEFAULT_ACCENT[choice.theme],
    }
  if ('preset' in choice) {
    const p = choice.preset.palette
    return {
      theme: { kind: 'palette', theme: CUSTOM_THEME, palette: p },
      colour: hueOfColour(p.accent ?? '') ?? 'violet',
    }
  }
  return {
    theme: { kind: 'palette', theme: CUSTOM_THEME, palette: choice.own },
    colour: hueOfColour(choice.own.accent ?? '') ?? choice.accent,
  }
}

/** The palette tile a look comes to: a built-in theme (Midnight when unset), a preset's id, or your own. */
export const paletteTileOf = (look: Look): string => {
  if (look.theme === CUSTOM_THEME) return presetFor(look.palette)?.id ?? OWN_TILE
  return look.theme && (THEMES as readonly string[]).includes(look.theme) ? look.theme : 'midnight'
}

/** The tile key of a saved theme: its profile id, or `saved:worn` for a snapshot worn without one. */
export const savedTile = (profile: string | undefined): string => `saved:${profile ?? 'worn'}`

/** The tile a theme names: Horizon, a palette's, or a saved theme's. */
export const tileOfRef = (ref: ThemeRef | undefined, colour?: Hue): string => {
  if (!ref || ref.kind === 'horizon') return HORIZON_TILE
  if (ref.kind === 'studio') return savedTile(ref.profile)
  return paletteTileOf({ theme: ref.theme, palette: ref.palette, colour })
}

/** A saved theme's tile: the profile when it is still there; a worn snapshot whose profile is gone has none. */
export interface SavedTile {
  key: string
  profile?: ThemeProfileResource
  spec: unknown
  worn: boolean
  /** mine to edit; the others are a gallery theme I wear or a snapshot whose profile is gone */
  mine: boolean
}

/**
 * The saved themes shown as tiles: mine (`me` is `owner` or `grant:<id>`), newest first as given,
 * then the one I wear when it is not mine (a gallery theme, or a snapshot whose profile is gone).
 */
export const savedTiles = (
  profiles: ThemeProfileResource[],
  me: string,
  worn: StudioLook | undefined,
): SavedTile[] => {
  const live = profiles.filter((r) => !r.deleted_at)
  const tiles: SavedTile[] = live
    .filter((r) => mayEdit(r, me))
    .map((r) => {
      const on = worn?.profile === r.id
      // the worn one shows what is on: the snapshot, not the profile as since edited
      return { key: savedTile(r.id), profile: r, spec: on ? worn.spec : r.spec.theme, worn: on, mine: true }
    })
  if (worn && !tiles.some((t) => t.worn)) {
    const r = worn.profile ? live.find((p) => p.id === worn.profile) : undefined
    tiles.push({ key: savedTile(worn.profile), profile: r, spec: worn.spec, worn: true, mine: false })
  }
  return tiles
}

/** A theme's name as a person reads it; a saved theme's is `studioName` (its profile's, else "Your theme"). */
export const themeName = (ref: ThemeRef, studioName?: string): string =>
  ref.kind === 'horizon'
    ? 'Horizon'
    : ref.kind === 'studio'
      ? (studioName ?? 'your theme')
      : paletteName({ theme: ref.theme, palette: ref.palette })

/** A palette's name as a person reads it ("Halloween", "Catppuccin Mocha", "Your own"). */
export const paletteName = (look: Look): string => {
  const tile = paletteTileOf(look)
  if (tile === OWN_TILE) return 'Your own'
  return PRESETS.find((p) => p.id === tile)?.label ?? THEME_SURFACES[tile as Theme].label
}
